import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiPassphraseRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { hibachiAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { tradeHistoryResponseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiPassphrase,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  // apiPassphrase は accountId として使用
  if (!apiPassphrase) {
    throw new ApiPassphraseRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    createHeaders,
    proxy,
  })

  // クエリパラメータを構築
  const query: Record<string, string> = {
    accountId: apiPassphrase,
    limit: config.limit.toString(),
  }

  // from/to は milliseconds なので、秒に変換
  if (from) {
    query.startTime = Math.floor(from / 1000).toString()
  }
  if (to) {
    query.endTime = Math.floor(to / 1000).toString()
  }

  const response = await client.run({
    method: 'GET',
    path: '/trade/account/trades',
    query,
    responseSchema: tradeHistoryResponseSchema,
  })

  // timestamp でソート（昇順）
  const sortedTrades = response.trades.sort((a, b) => a.timestamp - b.timestamp)

  // データ処理
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const trade of sortedTrades) {
    // timestamp を milliseconds に変換
    const timestampMs = trade.timestamp * 1000

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined
        ? timestampMs
        : Math.min(fetchedFrom, timestampMs)
    fetchedTo =
      fetchedTo === undefined ? timestampMs : Math.max(fetchedTo, timestampMs)

    // symbol から base/quote を抽出（例: "ETH/USDT-P" → base: "ETH", quote: "USDT"）
    const symbolParts = trade.symbol.replace(/-P$/, '').split('/')
    const base = symbolParts[0] ?? trade.symbol
    const quote = symbolParts[1] ?? 'USDT'

    // 実現損益がある場合
    if (trade.realizedPnl.value !== 0n) {
      const quoteAsset = getAsset(quote)
      if (!quoteAsset) {
        throw new AssetNotFoundException(quote)
      }

      actions.push({
        comment: `Realized PL of ${base}/${quote}`,
        evidence: 'system-rule',
        order: 0,
        source: `${hibachiAccountCategory}_${config.type}_pnl_${trade.id}_${trade.timestamp}`,
        type: trade.realizedPnl.value > 0n ? 'realize-profit' : 'realize-loss',
        timestamp: timestampMs,
        transfers: [
          {
            asset: quoteAsset,
            amount: absoluteValue(trade.realizedPnl),
            direction: trade.realizedPnl.value > 0n ? 'in' : 'out',
          },
        ],
      })
    }

    // 手数料がある場合
    if (trade.fee.value !== 0n) {
      // Hibachi の手数料は USDT で計算される
      const feeAsset = getAsset('USDT')
      if (!feeAsset) {
        throw new AssetNotFoundException('USDT')
      }

      actions.push({
        comment: `Trade Fee of ${base}/${quote}`,
        evidence: 'system-rule',
        order: 0,
        source: `${hibachiAccountCategory}_${config.type}_fee_${trade.id}_${trade.timestamp}`,
        type: trade.fee.value > 0n ? 'fee' : 'income',
        timestamp: timestampMs,
        transfers: [
          {
            asset: feeAsset,
            amount: absoluteValue(trade.fee),
            direction: trade.fee.value > 0n ? 'out' : 'in',
          },
        ],
      })
    }
  }

  // Sort actions by timestamp in ascending order
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: response.trades.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
